import type { UserStatus } from '@bp/bp-graphql-types';
import { ProfileRoles } from '@bp/pim-auth-constants';
import i18next from 'i18next';
import { uniqueByUuid } from './uniquByUuid';

export type ProfileType = {
  organizationRoles: Array<string | null>;
  uuid: string;
  organization: { uuid: string };
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  displayName?: string | null;
  userStatus: UserStatus;
};

export function getProfileRoleFromRoles(roles: string[]): ProfileRoles {
  if (!roles || roles.length === 0) return ProfileRoles.Other;
  if (roles.includes('TEACHER')) return ProfileRoles.Teacher;
  else if (roles.includes('STUDENT')) return ProfileRoles.Student;
  else if (roles.includes('PARENT')) return ProfileRoles.Parent;
  else if (roles.includes('OTHER')) return ProfileRoles.Other;
  return ProfileRoles.Other;
}

export function getProfileRoleTranslation(role: ProfileRoles | string): string {
  switch (role) {
    case 'TEACHER':
      return i18next.t('rolesInOrganization.teacher', { count: 1 });
    case 'STUDENT':
      return i18next.t('rolesInOrganization.student', { count: 1 });
    case 'PARENT':
      return i18next.t('rolesInOrganization.parent', { count: 2 });
    case 'OTHER':
      return i18next.t('rolesInOrganization.other');
    default:
      return role as string;
  }
}

export const extractProfilesByRoles = <T extends ProfileType = ProfileType>({
  profileRoles,
  profiles,
}: {
  profiles?: T[];
  profileRoles: ProfileRoles[];
}) => {
  const _profiles = uniqueByUuid(profiles ?? []);

  const extractProfilesByRole = (role: ProfileRoles): T[] =>
    _profiles?.filter((p) => p.organizationRoles?.includes(role)) ?? [];

  const extractProfilesWithoutRole = (): T[] =>
    _profiles?.filter((p) => !profileRoles.some((role) => p.organizationRoles?.includes(role))) ?? [];

  const profileElements: Record<ProfileRoles, T[]> = {} as Record<ProfileRoles, T[]>;

  profileRoles.forEach((role) => {
    profileElements[role] = _profiles ? extractProfilesByRole(role) : [];
  });

  if (profileRoles.includes(ProfileRoles.Other))
    profileElements[ProfileRoles.Other].push(...extractProfilesWithoutRole());

  return profileElements;
};
